import format from 'date-fns/format';
import { generateTitle } from 'utils/charts/common';
import {
    SCENARIO_ONE_TREND_Y_AXIS_AMOUNT,
    TREND_ANALYSIS_TITLE
} from 'utils/charts/constants';
import BlackCircle from 'assets/images/black-circle-icon.svg';
import OrangeCircle from 'assets/images/cream-orange-circle-icon.svg';
import { formatNumberToKMB, isNumber, numberFormat } from 'utils/common';

const portfolioLossesGraphic = {
    image: {
        type: 'image',
        style: {
            image: BlackCircle
        },
        cursor: 'default',
        bottom: '3.5%',
        width: 12,
        height: 12,
        top: '94.5%',
        left: '23.5%'
    },
    text: {
        type: 'text',
        style: {
            text: 'Portfolio Unrealized Losses',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '26.5%'
    }
};

const rmRevenueGraphic = {
    image: {
        type: 'image',
        style: {
            image: OrangeCircle
        },
        cursor: 'default',
        bottom: 23,
        top: '94.5%',
        left: '56.5%'
    },
    text: {
        type: 'text',
        style: {
            text: 'RM Revenue',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '59.5%'
    }
};

const tooltipForLineChart = () => {
    return {
        formatter: function (params) {
            const { name: createDate, value: revenue } = params;
            const colorForRevenue = -revenue > 0 ? '#15C988' : '#EB4560';

            return (
                '<span style="line-height: 30px;">Creation Date</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                createDate +
                '</span>' +
                '<br><span style="line-height: 30px;">Portfolio Unrealized Losses</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: ' +
                colorForRevenue +
                ';">' +
                `-$${numberFormat(revenue)}` +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};
const tooltipForBarChart = () => {
    return {
        formatter: function (params) {
            const { name: createDate, value: revenue } = params;
            const colorForRevenue = revenue > 0 ? '#15C988' : '#EB4560';

            return (
                '<span style="line-height: 30px;">Creation Date</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                createDate +
                '</span>' +
                '<br><span style="line-height: 30px;">RM Revenue</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: ' +
                colorForRevenue +
                ';">' +
                `$${numberFormat(revenue)}` +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};
export const scenarioOneTrendAnalysisChartOptions = (data) => {
    const validData = data
        ? data
              .filter(
                  (d) =>
                      d.month > 0 &&
                      d.year > 0 &&
                      isNumber(d.negativeDelta) &&
                      isNumber(d.totalRevenue)
              )
              .reverse()
        : [];
    const dateList = validData.map((d) =>
        format(new Date(d.year, d.month - 1, 1), 'MMM-yy')
    );
    const negativeDeltaList = validData.map((d) => d.negativeDelta);
    const revenueList = validData.map((d) => d.totalRevenue);

    return {
        title: generateTitle(TREND_ANALYSIS_TITLE),
        tooltip: {
            show: true,
            trigger: 'item'
        },
        xAxis: [
            {
                data: dateList ? dateList : [],
                axisTick: {
                    show: false
                },
                splitLine: {
                    show: false
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: '#E7E7E7'
                    }
                },
                axisLabel: {
                    show: true,
                    color: '#929292',
                    fontWeight: 400,
                    fontSize: 10
                },
                boundaryGap: ['0%', '0%']
            },
            {
                data: dateList ? dateList : [],
                nameLocation: 'center',
                nameTextStyle: {
                    color: '#7FADBF',
                    fontWeight: 600,
                    fontSize: 12,
                    width: '100%'
                },
                axisTick: {
                    show: false
                },
                splitLine: {
                    show: false
                },
                gridIndex: 1,
                axisLine: {
                    show: false
                },
                axisLabel: {
                    show: false
                },
                boundaryGap: ['0%', '0%']
            }
        ],
        yAxis: [
            {
                name: SCENARIO_ONE_TREND_Y_AXIS_AMOUNT,
                nameLocation: 'end',
                offset: 40,
                nameRotate: 90,
                nameTextStyle: {
                    color: '#7FADBF',
                    fontWeight: 600,
                    fontSize: 12
                },
                nameGap: -10,
                axisLabel: {
                    show: false
                },
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                }
            },
            {
                nameLocation: 'center',
                nameTextStyle: {
                    color: '#7FADBF',
                    fontWeight: 600,
                    fontSize: 12
                },
                nameGap: 40,
                axisLabel: {
                    show: false
                },
                gridIndex: 1,
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                }
            }
        ],
        series: [
            {
                type: 'bar',
                data: revenueList
                    ? revenueList.map((value) => Math.abs(value.toFixed(2)))
                    : [],
                xAxisIndex: 0,
                yAxisIndex: 0,
                label: {
                    show: true,
                    position: 'top',
                    fontSize: 10,
                    fontStyle: 'normal',
                    fontWeight: 700,
                    color: '#FFA08C',
                    formatter: function (params) {
                        return `$${formatNumberToKMB(params.value)}`;
                    }
                },
                emphasis: {
                    focus: 'self'
                },
                color: '#FFA08C',
                barWidth: 28,
                tooltip: tooltipForBarChart()
            },
            {
                type: 'line',
                symbolSize: [7, 7],
                xAxisIndex: 1,
                yAxisIndex: 1,
                data: negativeDeltaList
                    ? negativeDeltaList.map((value) =>
                          Math.abs(value.toFixed(2))
                      )
                    : [],
                lineStyle: {
                    color: '#222222'
                },
                itemStyle: {
                    color: '#929292'
                },
                emphasis: {
                    focus: 'self',
                    blurScope: 'series'
                },
                label: {
                    show: true,
                    position: 'top',
                    fontSize: 10,
                    fontWeight: 700,
                    lineheight: 14,
                    offset: [0, -4],
                    formatter: function (params) {
                        return `$${formatNumberToKMB(params.value)}`;
                    }
                },
                tooltip: tooltipForLineChart()
            }
        ],
        grid: [
            {
                bottom: '20%',
                left: '55px',
                width: '440px',
                height: '130px'
            },
            {
                bottom: '53%',
                left: '55px',
                width: '440px',
                height: '130px'
            }
        ],
        graphic: [
            portfolioLossesGraphic.image,
            portfolioLossesGraphic.text,
            rmRevenueGraphic.image,
            rmRevenueGraphic.text
        ]
    };
};
